import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FileText, RefreshCw, CheckCircle, Clock, ChevronLeft,
  AlertTriangle, TrendingUp, Shield, Users, ArrowRight, ShieldCheck, Lock,
} from 'lucide-react';
import { companiesApi, CompanyFramework } from '../../api';
import PageInfoButton from '@/components/PageInfoButton';
import { notify } from '@/core/notifications/notify';
import { auditPeriodApi } from '@/domains/compliance/lifecycle/VerificationPanel';
import { useModal, ConfirmModal } from '@components/modal';

function approvalPct(f: CompanyFramework) {
  return f.total_pages > 0 ? Math.round((f.approved_count / f.total_pages) * 100) : 0;
}

const CompanyFrameworkDetail: React.FC = () => {
  const { frameworkId } = useParams<{ frameworkId: string }>();
  const { openModal } = useModal();
  const [framework, setFramework] = useState<CompanyFramework | null>(null);
  const [loading, setLoading] = useState(true);
  const [openAuditPeriod, setOpenAuditPeriod] = useState<any | null>(null);
  const [requestingAudit, setRequestingAudit] = useState(false);

  useEffect(() => { load(); }, [frameworkId]);

  const load = async () => {
    if (!frameworkId) return;
    setLoading(true);
    try {
      // Fetch all frameworks and find the matching one
      // (no single-framework endpoint exists yet)
      const all = await companiesApi.getCompanyFrameworks();
      const found = all.find(f => f.id === parseInt(frameworkId)) || null;
      setFramework(found);
      // Also check if there's an open audit period for this assignment
      if (found) {
        try {
          const periods = await auditPeriodApi.listForCompany();
          const mine = periods.find((p: any) => p.framework_assignment_id === found.assignment_id && (p.status === 'open' || p.status === 'locked'));
          setOpenAuditPeriod(mine ?? null);
        } catch {
          // non-blocking
        }
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAudit = () => {
    if (!framework) return;
    const allApproved = framework.approved_count === framework.total_pages;
    if (!allApproved) return; // gated
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Request External Audit',
        message:
          `Open an external audit period for "${framework.name}"? This will LOCK all ${framework.total_pages} compliance pages immediately — no further edits while the auditor reviews.\n\n` +
          `What happens next:\n` +
          `• If a certificate is issued, the pages stay LOCKED permanently as the immutable certified record (this is what the certificate attests to).\n` +
          `• If the audit is cancelled before certification, the pages unlock and become editable again.\n\n` +
          `Only proceed if you and the framework owner agree the evidence is final.`,
        confirmText: 'Open Audit Period',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: async () => {
          setRequestingAudit(true);
          try {
            const result = await auditPeriodApi.open(framework.assignment_id);
            if (!result.success) {
              notify.error(result.message ?? 'Failed to open audit period');
              return;
            }
            notify.success(`Audit period opened. ${result.data?.pagesLocked ?? 0} pages locked.`);
            await load();
          } catch (e: any) {
            notify.error(e?.message ?? 'Failed to open audit period');
          } finally {
            setRequestingAudit(false);
          }
        },
        onCancel: () => {},
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!framework) {
    return (
      <div className="p-6 max-w-3xl mx-auto text-center py-20">
        <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <h2 className="text-lg font-semibold text-slate-700">Framework not found</h2>
        <p className="text-sm text-slate-400 mt-1">It may have been removed or you don't have access.</p>
        <Link to="/company/frameworks" className="mt-4 inline-flex items-center gap-2 text-sm text-indigo-600 hover:underline">
          <ChevronLeft className="w-4 h-4" /> Back to frameworks
        </Link>
      </div>
    );
  }

  const pct = approvalPct(framework);
  const progressPct = framework.total_pages > 0 ? {
    approved: (framework.approved_count / framework.total_pages) * 100,
    submitted: (framework.submitted_count / framework.total_pages) * 100,
    in_progress: (framework.in_progress_count / framework.total_pages) * 100,
  } : { approved: 0, submitted: 0, in_progress: 0 };

  const completionPct = framework.total_pages > 0
    ? Math.round(((framework.approved_count + framework.submitted_count + framework.in_progress_count) / framework.total_pages) * 100)
    : 0;

  const scoreColor = pct >= 80 ? 'text-emerald-600' : pct >= 50 ? 'text-blue-600' : 'text-amber-600';
  const scoreBg = pct >= 80 ? 'bg-emerald-50 border-emerald-200' : pct >= 50 ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200';

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/company/frameworks" className="text-slate-400 hover:text-slate-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
              <Link to="/company/frameworks" className="hover:text-indigo-600">Frameworks</Link>
              <span>/</span>
              <span className="text-slate-600">{framework.name}</span>
            </div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">{framework.name}</h1><PageInfoButton /></div>
            {framework.description && (
              <p className="text-slate-500 text-sm mt-0.5">{framework.description}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {framework.submitted_count > 0 && (
            <Link
              to="/audit/pages-review"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100"
              title="Internal auditor reviews submitted pages before they can be sent for external audit"
            >
              <CheckCircle className="w-4 h-4" />
              {framework.submitted_count} awaiting internal review
            </Link>
          )}
          <Link
            to="/company/compliance-pages"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg hover:bg-slate-50"
          >
            <Users className="w-4 h-4 text-slate-500" /> Manage Assignments
          </Link>
        </div>
      </div>

      {/* ── Score + meta ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Approval score */}
        <div className={`rounded-xl border p-6 flex items-center gap-5 ${scoreBg}`}>
          <div className={`text-5xl font-bold leading-none ${scoreColor}`}>{pct}%</div>
          <div>
            <div className="font-semibold text-slate-800">Approval Rate</div>
            <div className="text-xs text-slate-500 mt-0.5">{framework.approved_count} of {framework.total_pages} pages fully approved</div>
            <div className="text-xs text-slate-400 mt-1">
              Assigned {new Date(framework.assigned_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
        </div>

        {/* Status breakdown */}
        <div className="lg:col-span-2 bg-white border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Page Breakdown</h3>
          {/* Multi-segment bar */}
          <div className="flex gap-0.5 h-3 rounded-full overflow-hidden bg-slate-100 mb-3">
            <div className="bg-emerald-500 transition-all" style={{ width: `${progressPct.approved}%` }} />
            <div className="bg-amber-400 transition-all" style={{ width: `${progressPct.submitted}%` }} />
            <div className="bg-blue-400 transition-all" style={{ width: `${progressPct.in_progress}%` }} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Approved', value: framework.approved_count, dot: 'bg-emerald-500', text: 'text-emerald-700' },
              { label: 'Submitted', value: framework.submitted_count, dot: 'bg-amber-400', text: 'text-amber-700' },
              { label: 'In Progress', value: framework.in_progress_count, dot: 'bg-blue-400', text: 'text-blue-700' },
              { label: 'Not Started', value: framework.not_started_count, dot: 'bg-slate-200', text: 'text-slate-500' },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${s.dot}`} />
                <div>
                  <div className={`text-lg font-bold ${s.text}`}>{s.value}</div>
                  <div className="text-xs text-slate-400">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Progress milestones ──────────────────────────────────────────────── */}
      <div className="bg-white border rounded-xl p-5">
        <h3 className="font-semibold text-slate-800 mb-4">Progress Milestones</h3>
        <div className="space-y-4">
          {[
            {
              label: 'Work Started',
              desc: 'At least one page is in progress or further',
              done: framework.in_progress_count + framework.submitted_count + framework.approved_count > 0,
              icon: <TrendingUp className="w-4 h-4" />,
            },
            {
              label: 'First Submission',
              desc: 'At least one page submitted for review',
              done: framework.submitted_count + framework.approved_count > 0,
              icon: <FileText className="w-4 h-4" />,
            },
            {
              label: 'Halfway Approved',
              desc: '50%+ of pages approved',
              done: pct >= 50,
              icon: <Shield className="w-4 h-4" />,
            },
            {
              label: 'All Submitted',
              desc: 'Every page has been submitted for review',
              done: framework.not_started_count === 0 && framework.in_progress_count === 0,
              icon: <CheckCircle className="w-4 h-4" />,
            },
            {
              label: 'Fully Approved',
              desc: 'All pages approved — ready for external audit',
              done: pct === 100,
              icon: <CheckCircle className="w-4 h-4" />,
            },
            {
              label: 'Certified',
              desc: framework.certificate_number
                ? `Certificate ${framework.certificate_number} issued`
                : 'External auditor issues certificate after reviewing all controls',
              done: !!framework.certification_id,
              icon: <ShieldCheck className="w-4 h-4" />,
            },
          ].map((m, i, arr) => (
            <div key={m.label} className="flex items-start gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5
                ${m.done ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                {m.icon}
              </div>
              <div className="flex-1 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${m.done ? 'text-emerald-700' : 'text-slate-600'}`}>
                    {m.label}
                  </span>
                  {m.done && (
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                      Complete
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{m.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── External Audit / Certificate Action ──────────────────────────────── */}
      {(() => {
        const allApproved = framework.approved_count === framework.total_pages && framework.total_pages > 0;
        // Certificate already issued — top billing
        if (framework.certification_id) {
          return (
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-violet-900">Certificate Issued</div>
                <p className="text-sm text-violet-700 mt-0.5">
                  <span className="font-mono font-semibold">{framework.certificate_number}</span>
                  {framework.certification_issued_at && <> · issued {new Date(framework.certification_issued_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</>}
                  {framework.certification_expires_at && <> · expires {new Date(framework.certification_expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</>}
                </p>
                <p className="text-xs text-violet-600 mt-1">All {framework.total_pages} pages are locked as the immutable certified record.</p>
              </div>
            </div>
          );
        }
        if (openAuditPeriod) {
          return (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-indigo-900">External Audit In Progress</div>
                <p className="text-sm text-indigo-700 mt-0.5">
                  An external audit period is {openAuditPeriod.status === 'locked' ? 'locked' : 'open'} for this framework. All pages are locked until the audit closes or is cancelled.
                </p>
              </div>
            </div>
          );
        }
        if (allApproved) {
          return (
            <div className="bg-gradient-to-r from-emerald-50 to-indigo-50 border border-emerald-200 rounded-xl p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-slate-900">Ready for External Audit</div>
                <p className="text-sm text-slate-600 mt-0.5">
                  All {framework.total_pages} pages are approved. Open an audit period to lock the framework for external auditor review.
                </p>
              </div>
              <button
                onClick={handleRequestAudit}
                disabled={requestingAudit}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex-shrink-0"
              >
                <ShieldCheck className="w-4 h-4" />
                {requestingAudit ? 'Opening…' : 'Request External Audit'}
              </button>
            </div>
          );
        }
        return (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-slate-700">External Audit</div>
              <p className="text-sm text-slate-500 mt-0.5">
                All {framework.total_pages} pages must be approved before requesting an external audit. Currently {framework.approved_count} of {framework.total_pages} approved.
              </p>
            </div>
            <button
              disabled
              title="All pages must be approved before requesting external audit"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-slate-200 text-slate-400 rounded-lg cursor-not-allowed flex-shrink-0"
            >
              <ShieldCheck className="w-4 h-4" />
              Request External Audit
            </button>
          </div>
        );
      })()}

      {/* ── Quick links ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          {
            label: 'View Compliance Pages',
            desc: 'Manage page assignments for this framework',
            href: '/company/compliance-pages',
            icon: <FileText className="w-5 h-5 text-indigo-600" />,
            badge: null,
          },
          {
            label: 'Pending Internal Review',
            desc: framework.submitted_count > 0 ? `${framework.submitted_count} pages awaiting internal auditor review` : 'No pending submissions',
            href: framework.submitted_count > 0 ? '/audit/pages-review' : null,
            icon: <AlertTriangle className="w-5 h-5 text-amber-500" />,
            badge: framework.submitted_count > 0 ? framework.submitted_count : null,
          },
          {
            label: 'Compliance Timeline',
            desc: 'See activity history for this framework',
            href: '/compliance/timeline',
            icon: <Clock className="w-5 h-5 text-slate-400" />,
            badge: null,
          },
        ].map(link => (
          link.href ? (
            <Link key={link.label} to={link.href}
              className="bg-white border rounded-xl p-4 hover:shadow-md hover:border-indigo-200 transition-all flex items-start gap-3 group">
              <div className="mt-0.5">{link.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-slate-800 group-hover:text-indigo-700">{link.label}</span>
                  {link.badge && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">{link.badge}</span>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{link.desc}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 mt-0.5 flex-shrink-0" />
            </Link>
          ) : (
            <div key={link.label} className="bg-white border rounded-xl p-4 opacity-50 flex items-start gap-3">
              <div className="mt-0.5">{link.icon}</div>
              <div>
                <div className="font-medium text-sm text-slate-800">{link.label}</div>
                <p className="text-xs text-slate-400 mt-0.5">{link.desc}</p>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
};

export default CompanyFrameworkDetail;
